// src/features/analytics/useAnalyticsTopContent.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi, type AnalyticsViewScope } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS, type AnalyticsMetric } from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, TopContentData } from './types';

export interface UseAnalyticsTopContentResult {
  topContent: AnalyticsEnvelope<TopContentData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAnalyticsTopContent(
  scope: AnalyticsViewScope | null,
  range: UtcRange,
  metric: AnalyticsMetric,
  limit: number,
  /** IANA zone for local calendar days (dashboard ADR-009); undefined = UTC days. */
  tz?: string
): UseAnalyticsTopContentResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.topContent(
      scope?.kind ?? 'account',
      scope?.cid ?? 0,
      range.from,
      range.to,
      metric,
      limit,
      tz ?? 'utc'
    ),
    queryFn: () => AnalyticsApi.getTopContent(scope!, range, metric, limit, tz),
    enabled: scope !== null,
    staleTime: 60_000,
  });

  return {
    topContent: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
